/**
 * localStorage persistence for the React Query cache so key farm data survives
 * an app reload or kill while offline. Logout already calls localStorage.clear()
 * (services/auth.ts), which purges this persisted cache too.
 */
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import type { PersistQueryClientOptions } from "@tanstack/react-query-persist-client";

// Only these query roots are persisted: core farm data plus records/event detail
// screens, without writing the whole cache to disk (tiles, dashboards, etc.).
export const PERSISTED_QUERY_ROOTS = [
  // Farm structure
  "fields",
  "arableFields",
  "animalGroups",
  "farms",
  "stockClasses",
  "birthingPeriods",
  "defaultStockClasses",
  "users",
  "accessibleUsers",
  "usersWithFileAccess",
  // Readings & records (lists + detail)
  "readings",
  "diaryEntries",
  "yieldEvents",
  "feedEvents",
  "feedPurchaseEvents",
  "fertilizerEvents",
  "reseedEvents",
  "fieldPreparationEvents",
  "fileEntries",
  "farmerImageUploads",
  "feedBudgetPlans",
  // Programmes & evidence
  "programmeInvitations",
  "programmeActions",
  "actionCatalog",
  "grantCatalog",
  "quickAssessment",
  "actionEvidence",
  "eventFiles",
  "actionEventEvidence",
  "autoEvidence",
  // Single-event detail (Records drill-down)
  "yieldEvent",
  "feedEvent",
  "feedPurchaseEvent",
  "fertilizerEvent",
  "reseedEvent",
  "fieldPreparationEvent",
] as const;

export type PersistedQueryRoot = typeof PERSISTED_QUERY_ROOTS[number];

const PERSISTED_QUERY_ROOT_SET = new Set<string>(PERSISTED_QUERY_ROOTS);

export function isPersistedQueryRoot(root: string): root is PersistedQueryRoot {
  return PERSISTED_QUERY_ROOT_SET.has(root);
}

/**
 * Invalidate persisted React Query cache on mismatch (e.g. after breaking shape
 * changes or updates to PERSISTED_QUERY_ROOTS). Bump this string — do not tie
 * to app version, so routine releases keep offline cache when still compatible.
 */
export const PERSIST_CACHE_BUSTER = "4";

export const queryPersister = createSyncStoragePersister({
  // Guard for non-browser environments (e.g. node-based tests). With no
  // storage the persister no-ops.
  storage: typeof window !== "undefined" ? window.localStorage : undefined,
  key: "ruumi-react-query-cache",
});

export const persistOptions: Omit<PersistQueryClientOptions, "queryClient"> = {
  persister: queryPersister,
  maxAge: 1000 * 60 * 60 * 24, // 24 hours
  buster: PERSIST_CACHE_BUSTER,
  dehydrateOptions: {
    shouldDehydrateQuery: (query) => {
      const root = query.queryKey[0];
      return (
        query.state.status === "success" &&
        typeof root === "string" &&
        isPersistedQueryRoot(root)
      );
    },
  },
};
